import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isIOS } from "react-device-detect";
import FortuneLabPrivacyPolicyModal from "../../components/FortuneLabPrivacyPolicyModal";
import ThirdPartyConsentModal from "../../components/ThirdPartyConsentModal";
import BusinessInfoFooter from "../../components/BusinessInfoFooter";
import { usePixel, PixelInfo } from "../../hooks/usePixel";

// 패키지 타입
interface PackageInfo {
  id: number;
  packageName: string;
  packagePrice: number;
  personType?: 1 | 2;
  products?: { id: string; name: string; custInfoCount?: 1 | 2 }[];
}

// 캠페인 정보 타입
interface TrackingInfo {
  campaignName: string;
  trkId: string;
  personType: number; // 1 or 2
  packages: PackageInfo[];
  topImageUrls?: string[];
  bottomImageUrls?: string[];
  facebookPixelId?: string;
  kakaoPixelId?: string;
  googleAdsPixelId?: string;
  googleAdsEventSnippet?: string;
  daangnPixelId?: string;
  tiktokPixelId?: string;
  bankName?: string;
  bankAccount?: string;
  bankAccountHolder?: string;
  companyName?: string;
  purchaseButtonName?: string;
  showFooter?: boolean;
  companyNumber?: string;
  mailOrderNumber?: string;
  ceoName?: string;
  bizProfileName?: string;
  documentTitle?: string;
}

// 고객 정보 타입 (2인용에서 사용)
interface CustInfoState {
  name: string;
  solarType: "SOLAR" | "LUNAR" | "LUNAR_INTER";
  birthDate: string; // yyyy.mm.dd
  birthTime: string; // HH:mm
  sexType: "M" | "F";
  unknownTime: boolean;
}

// 1인용 폼 상태
interface SingleFormState {
  name: string;
  phone: string;
  birthDate: string;
  solarType: "SOLAR" | "LUNAR" | "LUNAR_INTER";
  sexType: "M" | "F";
  birthTime: string;
  unknownTime: boolean;
}

interface FormErrors {
  name?: string;
  phone?: string;
  birthDate?: string;
  birthTime?: string;
  packageId?: string;
  custInfos?: string[];
  agreement?: string;
}

const MAX_NAME_LENGTH = 20;
const MAX_PHONE_LENGTH = 11;

const FORTUNE_LAB_BASE_URL =
  process.env.REACT_APP_FORTUNE_LAB_BASE_URL || process.env.REACT_APP_BASE_URL || "";

const sanitizePhoneNumber = (value: string) => value.replace(/\D/g, "").slice(0, MAX_PHONE_LENGTH);

const formatPhoneWithHyphen = (value: string) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length === 11) {
    // 010-1234-5678
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  } else if (digits.length === 10) {
    // 010-123-4567
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return digits;
};

const formatBirthDate = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}.${digits.slice(4)}`;
  return `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6)}`;
};

const formatBirthTime = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
};

const isValidBirthDate = (value: string) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 8) return false;

  const year = parseInt(digits.slice(0, 4), 10);
  const month = parseInt(digits.slice(4, 6), 10);
  const day = parseInt(digits.slice(6, 8), 10);

  const currentYear = new Date().getFullYear();
  if (year < 1900 || year > currentYear) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  const daysInMonth = new Date(year, month, 0).getDate();
  if (day > daysInMonth) return false;

  return true;
};

const isValidBirthTime = (value: string) => {
  if (!value) return true; // 선택사항
  if (value === "unknown:unknown") return true; // 모름 선택
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 4) return false;

  const hour = parseInt(digits.slice(0, 2), 10);
  const minute = parseInt(digits.slice(2, 4), 10);

  if (hour < 0 || hour > 23) return false;
  if (minute < 0 || minute > 59) return false;

  return true;
};

const isValidKoreanPhoneNumber = (value: string) => {
  if (!/^01[016789]\d{7,8}$/.test(value)) {
    return false;
  }

  const subscriber = value.slice(3);
  if (/^(\d)\1+$/.test(subscriber)) {
    return false;
  }

  return true;
};

const createEmptyCustInfo = (): CustInfoState => ({
  name: "",
  solarType: "SOLAR",
  birthDate: "",
  birthTime: "",
  sexType: "M",
  unknownTime: false,
});

const getPackagePersonType = (
  pkg: PackageInfo | undefined,
  fallbackPersonType: number | undefined
): 1 | 2 => {
  if (pkg?.personType === 1 || pkg?.personType === 2) {
    return pkg.personType;
  }
  if (pkg?.products && pkg.products.length > 0) {
    return pkg.products.some((product) => product.custInfoCount === 2) ? 2 : 1;
  }
  return fallbackPersonType === 2 ? 2 : 1;
};

// 년도 옵션 생성 (1920 ~ 현재년도)
const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear - 1920 + 1 }, (_, i) => {
  const year = currentYear - i;
  return { value: year.toString(), label: `${year}년` };
});

// 월 옵션 생성 (1 ~ 12)
const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const month = (i + 1).toString().padStart(2, "0");
  return { value: month, label: `${i + 1}월` };
});

// 일 옵션 생성 (1 ~ 31)
const DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => {
  const day = (i + 1).toString().padStart(2, "0");
  return { value: day, label: `${i + 1}일` };
});

// 시간 옵션 생성 (00 ~ 23 + 모름)
const HOUR_OPTIONS = [
  { value: "unknown", label: "모름" },
  ...Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, "0");
    return { value: hour, label: `${hour}시` };
  }),
];

// 분 옵션 생성 (00 ~ 59, 1분 단위)
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => {
  const minute = i.toString().padStart(2, "0");
  return { value: minute, label: `${minute}분` };
});

// 생년월일 파싱 헬퍼 함수
const parseBirthDate = (birthDate: string) => {
  const digits = birthDate.replace(/\D/g, "");
  if (digits.length === 8) {
    return {
      year: digits.slice(0, 4),
      month: digits.slice(4, 6),
      day: digits.slice(6, 8),
    };
  }
  return { year: "", month: "", day: "" };
};

// 시간 파싱 헬퍼 함수
const parseBirthTime = (birthTime: string) => {
  const parts = birthTime.split(":");
  if (parts.length === 2) {
    return { hour: parts[0], minute: parts[1] };
  }
  return { hour: "", minute: "" };
};

// 작은 셀렉트 스타일 (년/월/일, 시/분용)
const selectSmallStyle: React.CSSProperties = {
  height: "44px",
  border: "1px solid #dddddd",
  borderRadius: "8px",
  padding: "0 24px 0 8px",
  fontSize: "14px",
  outline: "none",
  backgroundColor: "#fff",
  appearance: "none",
  WebkitAppearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 8px center",
  boxSizing: "border-box",
  flex: 1,
  minWidth: 0,
};

const PurchaseMainPage = () => {
  const navigate = useNavigate();
  const { trkId } = useParams<{ trkId: string }>();
  const resolvedTrkId = (trkId ?? "").trim();

  const [trackingInfo, setTrackingInfo] = useState<TrackingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pixelInfo, setPixelInfo] = useState<PixelInfo | null>(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // 1인용 폼 상태
  const [singleForm, setSingleForm] = useState<SingleFormState>({
    name: "",
    phone: "",
    birthDate: "",
    solarType: "SOLAR",
    sexType: "M",
    birthTime: "",
    unknownTime: false,
  });

  // 2인용 폼 상태
  const [phone, setPhone] = useState(""); // 신청자 연락처
  const [custInfos, setCustInfos] = useState<CustInfoState[]>([createEmptyCustInfo(), createEmptyCustInfo()]);

  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const [isPrivacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [isThirdPartyModalOpen, setThirdPartyModalOpen] = useState(false);
  const [isPrivacyAgreementChecked, setPrivacyAgreementChecked] = useState(false);
  const [isThirdPartyAgreementChecked, setThirdPartyAgreementChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { trackConversion } = usePixel(pixelInfo);

  const selectedPackage = trackingInfo?.packages.find((pkg) => pkg.id === selectedPackageId);
  const selectedPackagePersonType = selectedPackage
    ? getPackagePersonType(selectedPackage, trackingInfo?.personType)
    : 1;

  useEffect(() => {
    document.title = trackingInfo?.documentTitle || "구매 신청";
  }, [trackingInfo?.documentTitle]);

  useEffect(() => {
    if (!resolvedTrkId) return;
    const landingKey = `purchaseLandingLanding:${resolvedTrkId}`;
    if (localStorage.getItem(landingKey)) return;
    fetch(`${FORTUNE_LAB_BASE_URL}/purchase-landing/tracking/landing`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ trkId: resolvedTrkId }),
    }).catch((error) => console.error("purchase landing track error", error));
    localStorage.setItem(landingKey, "true");
  }, [resolvedTrkId]);

  // 캠페인 정보 조회
  useEffect(() => {
    if (!resolvedTrkId) {
      setError("유효하지 않은 링크입니다.");
      setLoading(false);
      return;
    }

    let isCancelled = false;

    const fetchTrackingInfo = async () => {
      try {
        const response = await fetch(
          `${FORTUNE_LAB_BASE_URL}/purchase-landing/${resolvedTrkId}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const pack = await response.json();
        if (isCancelled) return;

        // Pack 형태 응답 처리
        if (pack.is !== "Available" || !pack.contents) {
          throw new Error("캠페인을 찾을 수 없습니다.");
        }

        const data: TrackingInfo = pack.contents;
        setTrackingInfo(data);

        // 픽셀 정보 설정
        setPixelInfo({
          facebookPixelId: data.facebookPixelId ?? null,
          kakaoPixelId: data.kakaoPixelId ?? null,
          googleAdsPixelId: data.googleAdsPixelId ?? null,
          googleAdsEventSnippet: data.googleAdsEventSnippet ?? null,
          daangnPixelId: data.daangnPixelId ?? null,
          tiktokPixelId: data.tiktokPixelId ?? null,
        });

        setLoading(false);
      } catch (fetchError) {
        console.error("purchase-landing tracking info error", fetchError);
        if (!isCancelled) {
          setError("캠페인 정보를 불러오는데 실패했습니다.");
          setLoading(false);
        }
      }
    };

    fetchTrackingInfo();

    return () => {
      isCancelled = true;
    };
  }, [resolvedTrkId]);

  // 이미지 프리로드
  useEffect(() => {
    if (!trackingInfo) return;

    const allImageUrls = [...(trackingInfo.topImageUrls ?? []), ...(trackingInfo.bottomImageUrls ?? [])];
    if (allImageUrls.length === 0) {
      setImagesLoaded(true);
      return;
    }

    let loadedCount = 0;
    allImageUrls.forEach((url) => {
      const img = new Image();
      img.onload = img.onerror = () => {
        loadedCount++;
        if (loadedCount === allImageUrls.length) {
          setImagesLoaded(true);
        }
      };
      img.src = url;
    });
  }, [trackingInfo]);

  // iOS 패딩 조정
  useEffect(() => {
    const updateMainStyle = () => {
      const mainElement = document.querySelector(".main_container main");
      if (mainElement instanceof HTMLElement) {
        const isKakaoOS = navigator.userAgent.toLowerCase().includes("kakao");
        let additionalPadding = 0;

        if (isIOS && !isKakaoOS) {
          additionalPadding = 100;
        } else if (isIOS && isKakaoOS) {
          additionalPadding = 40;
        }

        mainElement.style.paddingBottom = `${additionalPadding}px`;
      }
    };

    updateMainStyle();
    window.addEventListener("resize", updateMainStyle);
    return () => window.removeEventListener("resize", updateMainStyle);
  }, []);

  // 1인용 폼 핸들러
  const handleSingleFormChange = (field: keyof SingleFormState) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const rawValue = event.target.value;
      let nextValue: string = rawValue;

      if (field === "name") {
        nextValue = rawValue.slice(0, MAX_NAME_LENGTH);
      } else if (field === "phone") {
        nextValue = sanitizePhoneNumber(rawValue);
      } else if (field === "birthDate") {
        nextValue = formatBirthDate(rawValue);
      } else if (field === "birthTime") {
        nextValue = formatBirthTime(rawValue);
      }

      setSingleForm((prev) => ({ ...prev, [field]: nextValue }));
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    };

  // 2인용 연락처 핸들러
  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPhone(sanitizePhoneNumber(event.target.value));
    setFormErrors((prev) => ({ ...prev, phone: undefined }));
  };

  // 2인용 고객정보 핸들러
  const handleCustInfoChange = (
    index: number,
    field: keyof CustInfoState,
    value: string
  ) => {
    setCustInfos((prev) => {
      const updated = [...prev];
      if (field === "name") {
        updated[index] = { ...updated[index], name: value.slice(0, MAX_NAME_LENGTH) };
      } else if (field === "birthDate") {
        updated[index] = { ...updated[index], birthDate: formatBirthDate(value) };
      } else if (field === "birthTime") {
        updated[index] = { ...updated[index], birthTime: formatBirthTime(value) };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return updated;
    });
  };

  const syncFormForPackageType = (nextPersonType: 1 | 2) => {
    if (selectedPackagePersonType === 1 && nextPersonType === 2) {
      setPhone((prev) => prev || singleForm.phone);
      setCustInfos((prev) => [
        {
          name: singleForm.name,
          solarType: singleForm.solarType,
          birthDate: singleForm.birthDate,
          birthTime: singleForm.birthTime,
          sexType: singleForm.sexType,
          unknownTime: singleForm.unknownTime,
        },
        prev[1] ?? createEmptyCustInfo(),
      ]);
      return;
    }

    if (selectedPackagePersonType === 2 && nextPersonType === 1) {
      const applicant = custInfos[0] ?? createEmptyCustInfo();
      setSingleForm((prev) => ({
        ...prev,
        name: applicant.name,
        phone,
        birthDate: applicant.birthDate,
        solarType: applicant.solarType,
        sexType: applicant.sexType,
        birthTime: applicant.birthTime,
        unknownTime: applicant.unknownTime,
      }));
    }
  };

  const validate = (): boolean => {
    const errors: FormErrors = {};
    const is1Person = selectedPackagePersonType === 1;

    if (is1Person) {
      // 1인용 검증
      if (!singleForm.name.trim()) {
        errors.name = "이름을 입력해주세요.";
      }

      const sanitizedPhone = sanitizePhoneNumber(singleForm.phone);
      if (!sanitizedPhone) {
        errors.phone = "연락처를 입력해주세요.";
      } else if (!isValidKoreanPhoneNumber(sanitizedPhone)) {
        errors.phone = "유효한 휴대전화번호를 입력해주세요.";
      }

      if (!singleForm.birthDate || !isValidBirthDate(singleForm.birthDate)) {
        errors.birthDate = "유효한 생년월일을 입력해주세요.";
      }

      if (singleForm.birthTime && !isValidBirthTime(singleForm.birthTime)) {
        errors.birthTime = "유효한 시간을 입력해주세요.";
      }
    } else {
      // 2인용 검증
      const sanitizedPhone = sanitizePhoneNumber(phone);
      if (!sanitizedPhone) {
        errors.phone = "신청자 연락처를 입력해주세요.";
      } else if (!isValidKoreanPhoneNumber(sanitizedPhone)) {
        errors.phone = "유효한 휴대전화번호를 입력해주세요.";
      }

      const custErrors: string[] = [];
      custInfos.forEach((info, index) => {
        const errorMessages: string[] = [];

        if (!info.name.trim()) {
          errorMessages.push("이름을 입력해주세요.");
        }
        if (!info.birthDate || !isValidBirthDate(info.birthDate)) {
          errorMessages.push("유효한 생년월일을 입력해주세요.");
        }
        if (info.birthTime && !isValidBirthTime(info.birthTime)) {
          errorMessages.push("유효한 시간을 입력해주세요.");
        }

        custErrors[index] = errorMessages.join(" ");
      });

      if (custErrors.some((e) => e)) {
        errors.custInfos = custErrors;
      }
    }

    // 패키지 선택 검증
    if (!selectedPackageId) {
      errors.packageId = "패키지를 선택해주세요.";
    }

    // 동의 검증
    if (!isPrivacyAgreementChecked || !isThirdPartyAgreementChecked) {
      errors.agreement = "개인정보 수집·이용 및 제3자 제공에 동의해주세요.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const is1Person = selectedPackagePersonType === 1;
      let submitData;

      if (is1Person) {
        // 1인용 데이터
        const birthDigits = singleForm.birthDate.replace(/\D/g, "");
        const timeDigits = singleForm.birthTime.replace(/\D/g, "");

        submitData = {
          name: singleForm.name.trim(),
          phone: formatPhoneWithHyphen(singleForm.phone),
          packageId: selectedPackageId,
          custInfos: [{
            sequence: 1,
            name: singleForm.name.trim(),
            solarType: singleForm.solarType,
            year: parseInt(birthDigits.slice(0, 4), 10),
            month: parseInt(birthDigits.slice(4, 6), 10),
            day: parseInt(birthDigits.slice(6, 8), 10),
            hour: timeDigits.length >= 2 ? parseInt(timeDigits.slice(0, 2), 10) : null,
            minute: timeDigits.length >= 4 ? parseInt(timeDigits.slice(2, 4), 10) : null,
            sexType: singleForm.sexType,
          }],
        };
      } else {
        // 2인용 데이터
        submitData = {
          name: custInfos[0].name.trim(),
          phone: formatPhoneWithHyphen(phone),
          packageId: selectedPackageId,
          custInfos: custInfos.map((info, index) => {
            const birthDigits = info.birthDate.replace(/\D/g, "");
            const timeDigits = info.birthTime.replace(/\D/g, "");

            return {
              sequence: index + 1,
              name: info.name.trim(),
              solarType: info.solarType,
              year: parseInt(birthDigits.slice(0, 4), 10),
              month: parseInt(birthDigits.slice(4, 6), 10),
              day: parseInt(birthDigits.slice(6, 8), 10),
              hour: timeDigits.length >= 2 ? parseInt(timeDigits.slice(0, 2), 10) : null,
              minute: timeDigits.length >= 4 ? parseInt(timeDigits.slice(2, 4), 10) : null,
              sexType: info.sexType,
            };
          }),
        };
      }

      const response = await fetch(
        `${FORTUNE_LAB_BASE_URL}/purchase-landing/${resolvedTrkId}/submit`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(submitData),
        }
      );

      if (!response.ok) {
        throw new Error("신청에 실패했습니다.");
      }

      const pack = await response.json();
      if (pack.is !== "Available" || !pack.contents) {
        throw new Error(pack.message || "신청에 실패했습니다.");
      }

      // 픽셀 전환 이벤트 발동
      trackConversion();

      // 결과 페이지로 이동 (계좌 정보 전달)
      navigate(`/purchase/${resolvedTrkId}/result`, {
        state: {
          bankName: trackingInfo?.bankName,
          bankAccount: trackingInfo?.bankAccount,
          bankAccountHolder: trackingInfo?.bankAccountHolder,
          packageName: trackingInfo?.packages.find((p) => p.id === selectedPackageId)?.packageName,
          packagePrice: trackingInfo?.packages.find((p) => p.id === selectedPackageId)?.packagePrice,
        },
      });
    } catch (submitError) {
      console.error("신청 실패", submitError);
      alert("신청에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || !imagesLoaded) {
    return (
      <div className="main_container">
        <main style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
          <p>로딩 중...</p>
        </main>
      </div>
    );
  }

  if (error || !trackingInfo) {
    return (
      <div className="main_container">
        <main style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
          <p>{error || "캠페인 정보를 찾을 수 없습니다."}</p>
        </main>
      </div>
    );
  }

  const topImageUrls = trackingInfo.topImageUrls ?? [];
  const bottomImageUrls = trackingInfo.bottomImageUrls ?? [];
  const is1Person = selectedPackagePersonType === 1;

  // 성별 버튼 스타일
  const genderButtonStyle = (isActive: boolean): React.CSSProperties => ({
    flex: 1,
    height: "52px",
    border: isActive ? "2px solid #4F46E5" : "1px solid #dddddd",
    borderRadius: "8px",
    backgroundColor: isActive ? "#EEF2FF" : "#fff",
    color: isActive ? "#4F46E5" : "#333",
    fontSize: "16px",
    fontWeight: isActive ? 600 : 400,
    cursor: "pointer",
    transition: "all 0.2s ease",
  });

  // 양/음력 선택 버튼 스타일
  const solarTypeButtonStyle = (isActive: boolean): React.CSSProperties => ({
    flex: 1,
    height: "44px",
    border: isActive ? "2px solid #4F46E5" : "1px solid #dddddd",
    borderRadius: "8px",
    backgroundColor: isActive ? "#EEF2FF" : "#fff",
    color: isActive ? "#4F46E5" : "#333",
    fontSize: "14px",
    fontWeight: isActive ? 600 : 400,
    cursor: "pointer",
    transition: "all 0.2s ease",
  });

  // 필드 타이틀 스타일
  const fieldTitleStyle: React.CSSProperties = {
    fontSize: "13px",
    fontWeight: 500,
    color: "#333",
    marginBottom: "6px",
    textAlign: "left",
  };

  // 1인용 폼 렌더링
  const render1PersonForm = () => (
    <>
      {/* 이름 */}
      <div style={{ width: "84%" }}>
        <div style={fieldTitleStyle}>이름</div>
        <input
          type="text"
          placeholder="이름을 입력하세요"
          value={singleForm.name}
          onChange={handleSingleFormChange("name")}
          autoComplete="name"
          maxLength={MAX_NAME_LENGTH}
          style={{ width: "100%" }}
        />
      </div>
      {formErrors.name && <div className="input_error">{formErrors.name}</div>}

      {/* 연락처 */}
      <div style={{ width: "84%" }}>
        <div style={fieldTitleStyle}>연락처</div>
        <input
          type="tel"
          placeholder="01012345678"
          value={singleForm.phone}
          onChange={handleSingleFormChange("phone")}
          autoComplete="tel"
          inputMode="numeric"
          maxLength={MAX_PHONE_LENGTH}
          style={{ width: "100%" }}
        />
      </div>
      {formErrors.phone && <div className="input_error">{formErrors.phone}</div>}

      {/* 성별 - 버튼 스타일 */}
      <div style={{ width: "84%" }}>
        <div style={fieldTitleStyle}>성별</div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            type="button"
            style={genderButtonStyle(singleForm.sexType === "M")}
            onClick={() => setSingleForm((prev) => ({ ...prev, sexType: "M" }))}
          >
            남성
          </button>
          <button
            type="button"
            style={genderButtonStyle(singleForm.sexType === "F")}
            onClick={() => setSingleForm((prev) => ({ ...prev, sexType: "F" }))}
          >
            여성
          </button>
        </div>
      </div>

      {/* 양/음력 */}
      <div style={{ width: "84%" }}>
        <div style={fieldTitleStyle}>양/음력</div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            type="button"
            style={solarTypeButtonStyle(singleForm.solarType === "SOLAR")}
            onClick={() => setSingleForm((prev) => ({ ...prev, solarType: "SOLAR" }))}
          >
            양력
          </button>
          <button
            type="button"
            style={solarTypeButtonStyle(singleForm.solarType === "LUNAR")}
            onClick={() => setSingleForm((prev) => ({ ...prev, solarType: "LUNAR" }))}
          >
            음력
          </button>
          <button
            type="button"
            style={solarTypeButtonStyle(singleForm.solarType === "LUNAR_INTER")}
            onClick={() => setSingleForm((prev) => ({ ...prev, solarType: "LUNAR_INTER" }))}
          >
            음력(윤달)
          </button>
        </div>
      </div>

      {/* 생년월일 */}
      <div style={{ width: "84%" }}>
        <div style={fieldTitleStyle}>생년월일</div>
        <div style={{ display: "flex", gap: "6px" }}>
          <select
            value={parseBirthDate(singleForm.birthDate).year}
            onChange={(e) => {
              const parsed = parseBirthDate(singleForm.birthDate);
              const newDate = e.target.value && parsed.month && parsed.day
                ? `${e.target.value}${parsed.month}${parsed.day}`
                : e.target.value ? `${e.target.value}0101` : "";
              setSingleForm((prev) => ({ ...prev, birthDate: newDate }));
              setFormErrors((prev) => ({ ...prev, birthDate: undefined }));
            }}
            style={selectSmallStyle}
          >
            <option value="">년</option>
            {YEAR_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={parseBirthDate(singleForm.birthDate).month}
            onChange={(e) => {
              const parsed = parseBirthDate(singleForm.birthDate);
              const year = parsed.year || currentYear.toString();
              const day = parsed.day || "01";
              const newDate = `${year}${e.target.value}${day}`;
              setSingleForm((prev) => ({ ...prev, birthDate: newDate }));
              setFormErrors((prev) => ({ ...prev, birthDate: undefined }));
            }}
            style={selectSmallStyle}
          >
            <option value="">월</option>
            {MONTH_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={parseBirthDate(singleForm.birthDate).day}
            onChange={(e) => {
              const parsed = parseBirthDate(singleForm.birthDate);
              const year = parsed.year || currentYear.toString();
              const month = parsed.month || "01";
              const newDate = `${year}${month}${e.target.value}`;
              setSingleForm((prev) => ({ ...prev, birthDate: newDate }));
              setFormErrors((prev) => ({ ...prev, birthDate: undefined }));
            }}
            style={selectSmallStyle}
          >
            <option value="">일</option>
            {DAY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>
      {formErrors.birthDate && <div className="input_error">{formErrors.birthDate}</div>}

      {/* 태어난 시간 */}
      <div style={{ width: "84%" }}>
        <div style={{ marginBottom: "6px" }}>
          <div style={fieldTitleStyle}>태어난 시간</div>
        </div>
        {(() => {
          const parsedHour = parseBirthTime(singleForm.birthTime).hour;
          const isUnknownHour = parsedHour === "unknown";
          return (
            <div style={{ display: "flex", gap: "6px" }}>
              <select
                value={parsedHour}
                onChange={(e) => {
                  const selectedHour = e.target.value;
                  if (selectedHour === "unknown") {
                    setSingleForm((prev) => ({ ...prev, birthTime: "unknown:unknown" }));
                  } else if (selectedHour === "") {
                    setSingleForm((prev) => ({ ...prev, birthTime: "" }));
                  } else {
                    setSingleForm((prev) => ({ ...prev, birthTime: `${selectedHour}:00` }));
                  }
                }}
                style={selectSmallStyle}
              >
                <option value="">시</option>
                {HOUR_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <select
                value={isUnknownHour ? "unknown" : parseBirthTime(singleForm.birthTime).minute}
                onChange={(e) => {
                  const parsed = parseBirthTime(singleForm.birthTime);
                  const hour = parsed.hour || "00";
                  const newTime = `${hour}:${e.target.value}`;
                  setSingleForm((prev) => ({ ...prev, birthTime: newTime }));
                }}
                disabled={isUnknownHour}
                style={{
                  ...selectSmallStyle,
                  opacity: isUnknownHour ? 0.5 : 1,
                }}
              >
                {isUnknownHour ? (
                  <option value="unknown">모름</option>
                ) : (
                  <>
                    <option value="">분</option>
                    {MINUTE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </>
                )}
              </select>
            </div>
          );
        })()}
      </div>
      {formErrors.birthTime && <div className="input_error">{formErrors.birthTime}</div>}
    </>
  );

  // 2인용 성별 버튼 스타일 (작은 버전)
  const genderButtonStyleSmall = (isActive: boolean): React.CSSProperties => ({
    flex: 1,
    height: "44px",
    border: isActive ? "2px solid #4F46E5" : "1px solid #dddddd",
    borderRadius: "8px",
    backgroundColor: isActive ? "#EEF2FF" : "#fff",
    color: isActive ? "#4F46E5" : "#333",
    fontSize: "14px",
    fontWeight: isActive ? 600 : 400,
    cursor: "pointer",
    transition: "all 0.2s ease",
  });

  // 2인용 폼 렌더링
  const render2PersonForm = () => (
    <>
      {/* 신청자 연락처 */}
      <div style={{ width: "84%" }}>
        <div style={fieldTitleStyle}>신청자 연락처</div>
        <input
          type="tel"
          placeholder="01012345678"
          value={phone}
          onChange={handlePhoneChange}
          onBlur={(e) => setPhone(sanitizePhoneNumber(e.target.value))}
          autoComplete="tel"
          inputMode="numeric"
          maxLength={MAX_PHONE_LENGTH}
          style={{ width: "100%" }}
        />
      </div>
      {formErrors.phone && <div className="input_error">{formErrors.phone}</div>}

      {/* 신청자 */}
      <div
        style={{
          padding: "12px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          marginBottom: "8px",
          width: "84%",
          boxSizing: "border-box",
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: "12px", color: "#4F46E5" }}>신청자</div>
        {/* 이름 */}
        <div style={{ marginBottom: "8px" }}>
          <div style={fieldTitleStyle}>이름</div>
          <input
            type="text"
            placeholder="이름을 입력하세요"
            value={custInfos[0].name}
            onChange={(e) => handleCustInfoChange(0, "name", e.target.value)}
            maxLength={MAX_NAME_LENGTH}
            style={{ width: "100%" }}
          />
        </div>
        {/* 성별 */}
        <div style={{ marginBottom: "8px" }}>
          <div style={fieldTitleStyle}>성별</div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              style={genderButtonStyleSmall(custInfos[0].sexType === "M")}
              onClick={() => setCustInfos((prev) => {
                const updated = [...prev];
                updated[0] = { ...updated[0], sexType: "M" };
                return updated;
              })}
            >
              남성
            </button>
            <button
              type="button"
              style={genderButtonStyleSmall(custInfos[0].sexType === "F")}
              onClick={() => setCustInfos((prev) => {
                const updated = [...prev];
                updated[0] = { ...updated[0], sexType: "F" };
                return updated;
              })}
            >
              여성
            </button>
          </div>
        </div>
        {/* 양/음력 */}
        <div style={{ marginBottom: "8px" }}>
          <div style={fieldTitleStyle}>양/음력</div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              style={solarTypeButtonStyle(custInfos[0].solarType === "SOLAR")}
              onClick={() => setCustInfos((prev) => {
                const updated = [...prev];
                updated[0] = { ...updated[0], solarType: "SOLAR" };
                return updated;
              })}
            >
              양력
            </button>
            <button
              type="button"
              style={solarTypeButtonStyle(custInfos[0].solarType === "LUNAR")}
              onClick={() => setCustInfos((prev) => {
                const updated = [...prev];
                updated[0] = { ...updated[0], solarType: "LUNAR" };
                return updated;
              })}
            >
              음력
            </button>
            <button
              type="button"
              style={solarTypeButtonStyle(custInfos[0].solarType === "LUNAR_INTER")}
              onClick={() => setCustInfos((prev) => {
                const updated = [...prev];
                updated[0] = { ...updated[0], solarType: "LUNAR_INTER" };
                return updated;
              })}
            >
              음력(윤달)
            </button>
          </div>
        </div>

        {/* 생년월일 */}
        <div style={{ marginBottom: "8px" }}>
          <div style={fieldTitleStyle}>생년월일</div>
          <div style={{ display: "flex", gap: "6px" }}>
            <select
              value={parseBirthDate(custInfos[0].birthDate).year}
              onChange={(e) => {
                const parsed = parseBirthDate(custInfos[0].birthDate);
                const newDate = e.target.value && parsed.month && parsed.day
                  ? `${e.target.value}${parsed.month}${parsed.day}`
                  : e.target.value ? `${e.target.value}0101` : "";
                setCustInfos((prev) => {
                  const updated = [...prev];
                  updated[0] = { ...updated[0], birthDate: newDate };
                  return updated;
                });
              }}
              style={selectSmallStyle}
            >
              <option value="">년</option>
              {YEAR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              value={parseBirthDate(custInfos[0].birthDate).month}
              onChange={(e) => {
                const parsed = parseBirthDate(custInfos[0].birthDate);
                const year = parsed.year || currentYear.toString();
                const day = parsed.day || "01";
                const newDate = `${year}${e.target.value}${day}`;
                setCustInfos((prev) => {
                  const updated = [...prev];
                  updated[0] = { ...updated[0], birthDate: newDate };
                  return updated;
                });
              }}
              style={selectSmallStyle}
            >
              <option value="">월</option>
              {MONTH_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              value={parseBirthDate(custInfos[0].birthDate).day}
              onChange={(e) => {
                const parsed = parseBirthDate(custInfos[0].birthDate);
                const year = parsed.year || currentYear.toString();
                const month = parsed.month || "01";
                const newDate = `${year}${month}${e.target.value}`;
                setCustInfos((prev) => {
                  const updated = [...prev];
                  updated[0] = { ...updated[0], birthDate: newDate };
                  return updated;
                });
              }}
              style={selectSmallStyle}
            >
              <option value="">일</option>
              {DAY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
        {/* 태어난 시간 */}
        <div>
          <div style={{ marginBottom: "6px" }}>
            <div style={fieldTitleStyle}>태어난 시간</div>
          </div>
          {(() => {
            const parsedHour = parseBirthTime(custInfos[0].birthTime).hour;
            const isUnknownHour = parsedHour === "unknown";
            return (
              <div style={{ display: "flex", gap: "6px" }}>
                <select
                  value={parsedHour}
                  onChange={(e) => {
                    const selectedHour = e.target.value;
                    setCustInfos((prev) => {
                      const updated = [...prev];
                      if (selectedHour === "unknown") {
                        updated[0] = { ...updated[0], birthTime: "unknown:unknown" };
                      } else if (selectedHour === "") {
                        updated[0] = { ...updated[0], birthTime: "" };
                      } else {
                        updated[0] = { ...updated[0], birthTime: `${selectedHour}:00` };
                      }
                      return updated;
                    });
                  }}
                  style={selectSmallStyle}
                >
                  <option value="">시</option>
                  {HOUR_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select
                  value={isUnknownHour ? "unknown" : parseBirthTime(custInfos[0].birthTime).minute}
                  onChange={(e) => {
                    const parsed = parseBirthTime(custInfos[0].birthTime);
                    const hour = parsed.hour || "00";
                    const newTime = `${hour}:${e.target.value}`;
                    setCustInfos((prev) => {
                      const updated = [...prev];
                      updated[0] = { ...updated[0], birthTime: newTime };
                      return updated;
                    });
                  }}
                  disabled={isUnknownHour}
                  style={{
                    ...selectSmallStyle,
                    opacity: isUnknownHour ? 0.5 : 1,
                  }}
                >
                  {isUnknownHour ? (
                    <option value="unknown">모름</option>
                  ) : (
                    <>
                      <option value="">분</option>
                      {MINUTE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            );
          })()}
        </div>
        {formErrors.custInfos?.[0] && (
          <div className="input_error" style={{ marginTop: "8px", width: "100%" }}>
            {formErrors.custInfos[0]}
          </div>
        )}
      </div>

      {/* 상대방 */}
      <div
        style={{
          padding: "12px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          marginBottom: "8px",
          width: "84%",
          boxSizing: "border-box",
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: "12px", color: "#7C3AED" }}>상대방</div>
        {/* 이름 */}
        <div style={{ marginBottom: "8px" }}>
          <div style={fieldTitleStyle}>이름</div>
          <input
            type="text"
            placeholder="이름을 입력하세요"
            value={custInfos[1].name}
            onChange={(e) => handleCustInfoChange(1, "name", e.target.value)}
            maxLength={MAX_NAME_LENGTH}
            style={{ width: "100%" }}
          />
        </div>
        {/* 성별 */}
        <div style={{ marginBottom: "8px" }}>
          <div style={fieldTitleStyle}>성별</div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              style={genderButtonStyleSmall(custInfos[1].sexType === "M")}
              onClick={() => setCustInfos((prev) => {
                const updated = [...prev];
                updated[1] = { ...updated[1], sexType: "M" };
                return updated;
              })}
            >
              남성
            </button>
            <button
              type="button"
              style={genderButtonStyleSmall(custInfos[1].sexType === "F")}
              onClick={() => setCustInfos((prev) => {
                const updated = [...prev];
                updated[1] = { ...updated[1], sexType: "F" };
                return updated;
              })}
            >
              여성
            </button>
          </div>
        </div>
        {/* 양/음력 */}
        <div style={{ marginBottom: "8px" }}>
          <div style={fieldTitleStyle}>양/음력</div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              style={solarTypeButtonStyle(custInfos[1].solarType === "SOLAR")}
              onClick={() => setCustInfos((prev) => {
                const updated = [...prev];
                updated[1] = { ...updated[1], solarType: "SOLAR" };
                return updated;
              })}
            >
              양력
            </button>
            <button
              type="button"
              style={solarTypeButtonStyle(custInfos[1].solarType === "LUNAR")}
              onClick={() => setCustInfos((prev) => {
                const updated = [...prev];
                updated[1] = { ...updated[1], solarType: "LUNAR" };
                return updated;
              })}
            >
              음력
            </button>
            <button
              type="button"
              style={solarTypeButtonStyle(custInfos[1].solarType === "LUNAR_INTER")}
              onClick={() => setCustInfos((prev) => {
                const updated = [...prev];
                updated[1] = { ...updated[1], solarType: "LUNAR_INTER" };
                return updated;
              })}
            >
              음력(윤달)
            </button>
          </div>
        </div>

        {/* 생년월일 */}
        <div style={{ marginBottom: "8px" }}>
          <div style={fieldTitleStyle}>생년월일</div>
          <div style={{ display: "flex", gap: "6px" }}>
            <select
              value={parseBirthDate(custInfos[1].birthDate).year}
              onChange={(e) => {
                const parsed = parseBirthDate(custInfos[1].birthDate);
                const newDate = e.target.value + (parsed.month || "01") + (parsed.day || "01");
                setCustInfos((prev) => {
                  const updated = [...prev];
                  updated[1] = { ...updated[1], birthDate: newDate };
                  return updated;
                });
              }}
              style={selectSmallStyle}
            >
              <option value="">년</option>
              {YEAR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              value={parseBirthDate(custInfos[1].birthDate).month}
              onChange={(e) => {
                const parsed = parseBirthDate(custInfos[1].birthDate);
                const newDate = (parsed.year || "2000") + e.target.value + (parsed.day || "01");
                setCustInfos((prev) => {
                  const updated = [...prev];
                  updated[1] = { ...updated[1], birthDate: newDate };
                  return updated;
                });
              }}
              style={selectSmallStyle}
            >
              <option value="">월</option>
              {MONTH_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              value={parseBirthDate(custInfos[1].birthDate).day}
              onChange={(e) => {
                const parsed = parseBirthDate(custInfos[1].birthDate);
                const newDate = (parsed.year || "2000") + (parsed.month || "01") + e.target.value;
                setCustInfos((prev) => {
                  const updated = [...prev];
                  updated[1] = { ...updated[1], birthDate: newDate };
                  return updated;
                });
              }}
              style={selectSmallStyle}
            >
              <option value="">일</option>
              {DAY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
        {/* 태어난 시간 */}
        <div>
          <div style={{ marginBottom: "6px" }}>
            <div style={fieldTitleStyle}>태어난 시간</div>
          </div>
          {(() => {
            const parsedHour = parseBirthTime(custInfos[1].birthTime).hour;
            const isUnknownHour = parsedHour === "unknown";
            return (
              <div style={{ display: "flex", gap: "6px" }}>
                <select
                  value={parsedHour}
                  onChange={(e) => {
                    const selectedHour = e.target.value;
                    setCustInfos((prev) => {
                      const updated = [...prev];
                      if (selectedHour === "unknown") {
                        updated[1] = { ...updated[1], birthTime: "unknown:unknown" };
                      } else if (selectedHour === "") {
                        updated[1] = { ...updated[1], birthTime: "" };
                      } else {
                        updated[1] = { ...updated[1], birthTime: `${selectedHour}:00` };
                      }
                      return updated;
                    });
                  }}
                  style={selectSmallStyle}
                >
                  <option value="">시</option>
                  {HOUR_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select
                  value={isUnknownHour ? "unknown" : parseBirthTime(custInfos[1].birthTime).minute}
                  onChange={(e) => {
                    const parsed = parseBirthTime(custInfos[1].birthTime);
                    const hour = parsed.hour || "00";
                    const newTime = `${hour}:${e.target.value}`;
                    setCustInfos((prev) => {
                      const updated = [...prev];
                      updated[1] = { ...updated[1], birthTime: newTime };
                      return updated;
                    });
                  }}
                  disabled={isUnknownHour}
                  style={{
                    ...selectSmallStyle,
                    opacity: isUnknownHour ? 0.5 : 1,
                  }}
                >
                  {isUnknownHour ? (
                    <option value="unknown">모름</option>
                  ) : (
                    <>
                      <option value="">분</option>
                      {MINUTE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            );
          })()}
        </div>
        {formErrors.custInfos?.[1] && (
          <div className="input_error" style={{ marginTop: "8px", width: "100%" }}>
            {formErrors.custInfos[1]}
          </div>
        )}
      </div>
    </>
  );

  // 패키지 선택 렌더링
  const renderPackageSelection = () => (
    trackingInfo.packages.length > 0 && (
      <div style={{ width: "84%", marginTop: "12px", textAlign: "left" }}>
        <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "10px" }}>패키지 선택</div>
        {trackingInfo.packages.map((pkg) => {
          const packagePersonType = getPackagePersonType(pkg, trackingInfo.personType);

          return (
            <label
              key={pkg.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "8px",
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="package"
                checked={selectedPackageId === pkg.id}
                onChange={() => {
                  syncFormForPackageType(packagePersonType);
                  setSelectedPackageId(pkg.id);
                  setFormErrors((prev) => ({
                    ...prev,
                    packageId: undefined,
                    name: undefined,
                    phone: undefined,
                    birthDate: undefined,
                    birthTime: undefined,
                    custInfos: undefined,
                  }));
                }}
                style={{ margin: 0, flexShrink: 0, width: "16px", height: "16px" }}
              />
              <span style={{ fontSize: "14px" }}>
                {pkg.packageName} - {pkg.packagePrice.toLocaleString()}원
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  padding: "2px 8px",
                  borderRadius: "999px",
                  backgroundColor: packagePersonType === 1 ? "#dbeafe" : "#ede9fe",
                  color: packagePersonType === 1 ? "#1e40af" : "#6d28d9",
                  fontSize: "12px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {packagePersonType}인
              </span>
            </label>
          );
        })}
        {formErrors.packageId && <div className="input_error" style={{ width: "100%" }}>{formErrors.packageId}</div>}
      </div>
    )
  );

  return (
    <>
      <div className="main_container">
        <main className="landing">
          {/* 상단 이미지 */}
          {topImageUrls.length > 0 && (
            <div style={{ width: "100%", marginBottom: "24px", display: "flex", flexDirection: "column" }}>
              {topImageUrls.map((url, index) => (
                <img key={`top-${index}`} src={url} alt={`상단 이미지 ${index + 1}`} style={{ width: "100%" }} />
              ))}
            </div>
          )}

          <form className="fortune_lab_form" onSubmit={handleSubmit}>
            {/* 1인용/2인용 폼 */}
            {is1Person ? render1PersonForm() : render2PersonForm()}

            {/* 패키지 선택 */}
            {renderPackageSelection()}

            {/* 동의 체크 */}
            <div className="privacy_agreement">
              <label className="privacy_agreement_checkbox">
                <input
                  type="checkbox"
                  checked={isPrivacyAgreementChecked}
                  onChange={(e) => setPrivacyAgreementChecked(e.target.checked)}
                />
                <span className="privacy_agreement_text">
                  개인정보 수집·이용에 동의합니다
                  <button
                    type="button"
                    className="privacy_link_button privacy_agreement_link"
                    onClick={() => setPrivacyModalOpen(true)}
                  >
                    개인정보 처리방침
                  </button>
                </span>
              </label>
              <label className="privacy_agreement_checkbox">
                <input
                  type="checkbox"
                  checked={isThirdPartyAgreementChecked}
                  onChange={(e) => setThirdPartyAgreementChecked(e.target.checked)}
                />
                <span className="privacy_agreement_text">
                  제3자 제공에 동의합니다
                  <button
                    type="button"
                    className="privacy_link_button privacy_agreement_link"
                    onClick={() => setThirdPartyModalOpen(true)}
                  >
                    자세히 보기
                  </button>
                </span>
              </label>
            </div>
            {formErrors.agreement && <div className="input_error">{formErrors.agreement}</div>}

            <button
              type="submit"
              className="kakao_button"
              disabled={isSubmitting}
              style={{ marginTop: "12px", marginBottom: "24px" }}
            >
              {isSubmitting ? "신청 중..." : (trackingInfo.purchaseButtonName || "구매 신청하기")}
            </button>
          </form>

          {/* 하단 이미지 */}
          {bottomImageUrls.length > 0 && (
            <div style={{ width: "100%", marginTop: "24px", display: "flex", flexDirection: "column" }}>
              {bottomImageUrls.map((url, index) => (
                <img key={`bottom-${index}`} src={url} alt={`하단 이미지 ${index + 1}`} style={{ width: "100%" }} />
              ))}
            </div>
          )}

          {trackingInfo.showFooter !== false && (
            <BusinessInfoFooter
              companyNumber={trackingInfo.companyNumber}
              mailOrderNumber={trackingInfo.mailOrderNumber}
              ceoName={trackingInfo.ceoName}
              bizProfileName={trackingInfo.bizProfileName}
              companyName={trackingInfo.companyName}
            />
          )}
        </main>
      </div>

      <FortuneLabPrivacyPolicyModal
        open={isPrivacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        companyName={trackingInfo.companyName ?? null}
        contactName={null}
        contactTel={null}
        contactEmail={null}
        useDefaultFallback={false}
      />
      <ThirdPartyConsentModal open={isThirdPartyModalOpen} onClose={() => setThirdPartyModalOpen(false)} />
    </>
  );
};

export default PurchaseMainPage;
